import React, { useEffect, useLayoutEffect, useState } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import makeStyles from '@mui/styles/makeStyles'
import { _theme } from './_theme'
import TextField from '@mui/material/TextField'
import { Autocomplete, Typography } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import PropTypes from 'prop-types'
import Chip from '@mui/material/Chip';
import { expandSelectionWithPastedText } from './_filters';
import { useDebounce } from './_helpers';

/**
 * @param {Object} props
 * @param {string} label
 * @param {boolean} disabled
 * @param {Object} style CSS props applied to the container
 * @param {string} id
 * @param {number} limitTags
 * @param {Array=} selectedOptions
 * @param {boolean=} showSelectAll
 * @param {function(string): Promise<Array<string>>} fetchOptions
 * @param {function(Object): string} getOptionLabel
 * @param {function()} updateHeight
 * @param {function(Array<FiltersAutocompleteOption>, boolean)} onChange(selection, selectedAll)
 * @return {JSX.Element}
 * @constructor
 */
export const FiltersAutocomplete = ({ label, disabled, style, id, limitTags = 5, selectedOptions, showSelectAll, fetchOptions, getOptionLabel, updateHeight, onChange, limitCheck }) => {

    const [options, setOptions] = useState([]);
    const [selectedValues, setSelectedValues] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [inputText, setInputText] = useState('');
    const debouncedTextInput = useDebounce(inputText, 1000);
    const [pastedText, setPastedText] = useState('');
    const [userVal, setUserVal] = useState('No Option')
    const labelId = id ? `${id}-label` : undefined;
    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    // theming for checkboxes inside autocomplete
    const useStyles = makeStyles(() => ({
        root: {
            color: _theme.palette.primary.main,
            '&$checked': {
                color: _theme.palette.primary.main,
            },
        },
        checked: {},
    }));
    const classes = useStyles();

    useLayoutEffect(() => {
        if (updateHeight) {
            setTimeout(() => {
                updateHeight();
            }, 1);
        }
    }, [open, selectedValues]);

    // set the internal selection when it is given externally upon component initialization
    useEffect(() => {
        if (showSelectAll) {
            setSelectedValues([{ description: 'Select all' }]);
        } else if (selectedOptions) {
            setSelectedValues(selectedOptions);
        }
    }, [selectedOptions, showSelectAll]);

    const fetchAndSetOptions = (debounceText) => {

        setLoading(true);
        fetchOptions(debounceText)
            .then((options) => {
                // sort and add select all option
                options?.sort((a, b) => (getOptionLabel(a).localeCompare(getOptionLabel(b))));
                options?.length > limitCheck && options.unshift({ description: 'Select all' });
                setOptions(options);

            })
            .finally(() => {
                setLoading(false);
            });
    }

    const handleOpen = () => {
        setOpen(true);
        if (!options.length && debouncedTextInput?.length >= limitCheck) {

            fetchAndSetOptions(debouncedTextInput)
        }
    };
    useEffect(() => {
        if (debouncedTextInput?.length >= limitCheck) {
            fetchAndSetOptions(debouncedTextInput);
            setUserVal("No Option");
        }
        else {
            setUserVal("Please Provide Input");

        }
    }, [debouncedTextInput])
    const handleOnChange = (newSelection) => {
        let exportedSelection = newSelection;
        // Select all case, export all options
        const selectedAll = newSelection.findIndex((item) => item.description === 'Select all') > -1;
        if (selectedAll) {
            exportedSelection = options.slice(1);
        }
        setSelectedValues(newSelection);
        onChange(exportedSelection, selectedAll);
    };

    // useEffect to wait that parsedText and optionsArray are up-to-date
    useEffect(() => {
        // Add limitcheck property to set a input text value 
        if (pastedText.length > limitCheck && options.length > 0 && !loading) {
            const newSelectedValues = expandSelectionWithPastedText(pastedText, options, selectedValues);
            handleOnChange(newSelectedValues);
            setPastedText('');
            // clear inputText on the next frame
            setTimeout(() => {
                setInputText('');
                setOpen(false);
            }, 0);
        }
    }, [pastedText, options, loading]);

    return <ThemeProvider theme={_theme}>
        <div style={style}>
            {label && <Typography id={labelId}>{label}</Typography>}
            <Autocomplete
                id={id}
                clearText='Clear'
                closeText='Close'
                noOptionsText={userVal}
                multiple
                limitTags={limitTags}
                options={options}
                value={selectedValues}
                disabled={disabled}
                disableCloseOnSelect
                open={open}
                loading={loading}
                loadingText={'Loading...'}
                onChange={(e, v) => {
                    handleOnChange(v);
                }}
                onPaste={(e, v) => {
                    const parsedText = e.clipboardData.getData('text');
                    // trigger processing of pasted values list, or leave pasted value as is if is not a list that is pasted
                    if (parsedText.includes(';')) {
                        setPastedText(parsedText);
                    }
                }}
                onOpen={handleOpen}
                onFocus={() => {
                    setOpen(true);
                }}
                onBlur={() => {
                    setOpen(false);
                    // update height after applying the limitTags prop
                    if (updateHeight) {
                        setTimeout(() => {
                            updateHeight();
                        }, 1);
                    }
                }}
                style={style}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={(opt, value) => {
                    return getOptionLabel(opt) === getOptionLabel(value);
                }}
                renderOption={(internalProps, option, state) => {
                    const optionLabel = getOptionLabel(option);
                    const isSelected = selectedValues.findIndex((item) => {
                        const label = getOptionLabel(item);
                        return label === optionLabel || label === 'Select all';
                    }) > -1;
                    return <li {...internalProps}>
                        <Checkbox
                            classes={{
                                root: classes.root,
                                checked: classes.checked,
                            }}
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={isSelected}
                        />
                        {optionLabel}
                    </li>;
                }}
                inputValue={inputText}
                onInputChange={(event, value, reason) => {
                    setInputText(value);
                }}
                renderInput={(params) => {
                    return <TextField
                        {...params}
                        variant='standard'
                        color='primary'              
                        inputProps={{
                        ...params.inputProps,
                        'aria-label': label || id
                        }}
                    />;
                }}
                renderTags={(value, getTagProps) => {
                    return value.map((option, index) => {
                        return <Chip
                            color={option.notValid ? 'secondary' : undefined}
                            label={getOptionLabel(option)}
                            {...getTagProps({ index })}
                        />;
                    });
                }}
            />
        </div>
    </ThemeProvider>;
};

FiltersAutocomplete.propTypes = {
    context: PropTypes.object,
    options: PropTypes.array,
    selectedOptions: PropTypes.array,
    showSelectAll: PropTypes.bool,
    label: PropTypes.string,
    style: PropTypes.object,
    disabled: PropTypes.bool,
    id: PropTypes.string,
    limitTags: PropTypes.number,
    fetchOptions: PropTypes.func,
    getOptionLabel: PropTypes.func,
    onChange: PropTypes.func,
    updateHeight: PropTypes.func,
};



